import React, { useEffect, useRef, useState, useCallback } from "react"
import { Button } from "../../shared/Button"
import { Dialog } from "@reach/dialog"
import "@reach/dialog/styles.css"
import { Elements } from "@stripe/react-stripe-js"
import { loadStripe, Stripe } from "@stripe/stripe-js"
import { useContext } from "react"
import { UserContext } from "../../HomeApp"
import {
  areTicketsAvailable,
  getTicketAvailabilityMessage,
  isEventOver,
  formatPrice,
} from "../../../util/ticketUtils"
import { CheckoutForm } from "../CheckoutForm"
import { BandInterestPrompt, qualifyingBandInterestLineup } from "../BandInterestPrompt"
import { AlternatePricingDisplay } from "../AlternatePricingDisplay"
import { TicketEventInfo } from "../TicketEventInfo"
import InterestedBlock from "@components/posts/InterestedBlock"
import {
  useMyTicketsForPost,
  ticketPurchaseQueryKeys,
} from "../../../hooks/useTicketPurchase"
import { TicketPurchaseUtils } from "../../../requests/ticketPurchases"
import {
  useCreateTicketPurchase,
  useUpdateTicketPurchase,
} from "../../../hooks/useTicketPurchaseMutations"
import { useQueryClient } from "react-query"
import { toast } from "react-toastify"
import Pluralize from "react-pluralize"

const MAX_PURCHASED_TICKETS_PER_SHOW = 4

interface TicketPurchaseComponentProps {
  bandId: number
  bandName: string
  userName: string
  userDefaultPaymentMethodLastFour: string
  userDefaultPaymentMethodExpired?: boolean
  postId: number
  ticketPrice: number
  maxTickets: number
  ticketsSold: number
  eventEndDate: string
  autoOpenDialog?: boolean
  ticketPriceAtDoor?: number
  maxTicketsPerUser?: number
  concert
}

const PRICING_TYPE_FREE = "free"
const PRICING_TYPE_SLIDING_SCALE = "sliding_scale"

interface TicketPurchase {
  id: number
  status: string
  payment_method?: string
  client_secret?: string
  ticket_id?: string
  amount?: number
  redeemed?: boolean
}

export const TicketPurchaseComponent: React.FC<
  TicketPurchaseComponentProps
> = ({
  bandId,
  bandName,
  userName,
  userDefaultPaymentMethodLastFour,
  userDefaultPaymentMethodExpired = false,
  postId,
  ticketPrice,
  maxTickets,
  ticketsSold,
  eventEndDate,
  autoOpenDialog = false,
  ticketPriceAtDoor,
  maxTicketsPerUser = MAX_PURCHASED_TICKETS_PER_SHOW,
  concert,
}) => {
  const { spk, user } = useContext(UserContext)
  const stripePromiseRef = useRef<Promise<Stripe | null>>()
  // maxTickets prop represents total tickets available for the show
  const ticketsAvailableForShow = maxTickets
  // Get band_nada_max_tickets_per_user from content_hash if it exists, otherwise use the constant
  const [ticketPurchase, setTicketPurchase] = useState<TicketPurchase | null>(
    null,
  )
  const [showDialog, setShowDialog] = useState<boolean>(false)
  const [lastFour, setLastFour] = useState<string>(
    userDefaultPaymentMethodLastFour,
  )
  // If card is expired, start with new card flow
  const [isNewCard, setIsNewCard] = useState(userDefaultPaymentMethodExpired)

  // Update lastFour when prop changes (e.g., after purchase on another event)
  // Also reset isNewCard if we now have a valid saved card
  useEffect(() => {
    // Only log and update if values actually changed to prevent excessive re-renders
    const lastFourChanged = userDefaultPaymentMethodLastFour !== lastFour
    const expiredChanged = userDefaultPaymentMethodExpired !== isNewCard

    if (userDefaultPaymentMethodLastFour && lastFourChanged) {
      setLastFour(userDefaultPaymentMethodLastFour)
    }
    // If we have a saved card and it's not expired, we shouldn't be in new card mode
    if (!userDefaultPaymentMethodExpired && expiredChanged) {
      setIsNewCard(false)
    }
  }, [
    userDefaultPaymentMethodLastFour,
    userDefaultPaymentMethodExpired,
    lastFour,
  ])
  const [quantity, setQuantity] = useState<number>(1)
  const [checkoutCompleted, setCheckoutCompleted] = useState<boolean>(false)
  const [showBandInterestPrompt, setShowBandInterestPrompt] =
    useState<boolean>(false)
  const [bandInterestTicketPurchaseId, setBandInterestTicketPurchaseId] =
    useState<number | null>(null)
  const pricingType: string = (concert as any).pricing_type ?? ""
  const isFreeTicket = pricingType === PRICING_TYPE_FREE
  const isSlidingScale = pricingType === PRICING_TYPE_SLIDING_SCALE
  const priceMax: number | null = (concert as any).ticket_price_max ?? null
  const [chosenPrice, setChosenPrice] = useState<number | null>(null)
  const effectiveMaxTicketsPerUser = isFreeTicket ? 1 : maxTicketsPerUser
  const closeDialog = () => {
    setShowDialog(false)
    // Reset checkout completed state when dialog is closed
    setCheckoutCompleted(false)
    // Otherwise the free-ticket fallback dialog below (which watches for
    // showBandInterestPrompt while showDialog is false) would immediately
    // pop back up as a standalone modal
    setShowBandInterestPrompt(false)
    // Remove the URL parameter when dialog is closed to prevent it from reopening
    const url = new URL(window.location.href)
    if (url.searchParams.has("auto-open-ticket-purchase")) {
      url.searchParams.delete("auto-open-ticket-purchase")
      window.history.replaceState({}, "", url.toString())
    }
  }

  // Use the hook to get the user's tickets for this post
  const { data: myTicketsData, isLoading: isLoadingTicket } =
    useMyTicketsForPost(postId)

  // Calculate derived values from the hook data
  const userTicketPurchases = myTicketsData?.ticket_purchases || []
  // Get the first ticket purchase (for backward compatibility with existing code that uses userTicket)
  const userTicket = userTicketPurchases[0]
  // Count completed ticket purchases (backend my_tickets endpoint returns completed tickets,
  // but we filter to be safe in case backend logic changes)
  const ticketsPurchasedByUserCount = userTicketPurchases.filter((ticket) =>
    TicketPurchaseUtils.isCompleted(ticket),
  ).length
  const userHasAnyTicketPurchase = userTicketPurchases.length > 0
  const userTicketPurchaseStatus = userTicket?.status

  // Calculate if tickets are available
  const ticketsAvailable = areTicketsAvailable(
    ticketsAvailableForShow,
    ticketsSold,
  )

  // Check if event is over
  const eventOver = isEventOver(eventEndDate)

  // A fan can cancel/refund their own ticket up until the show starts, not
  // all the way through to the show ending - once it's started, cancel
  // yourself at the door, don't just stop showing up. Prefer active_date
  // (e.g. a rescheduled show) over start_dt, same fallback CalendarEvent.tsx
  // already uses.
  const eventStarted = isEventOver(concert?.active_date || concert?.start_dt)

  // Get ticket availability message
  const availabilityMessage = getTicketAvailabilityMessage(
    ticketsAvailableForShow,
    ticketsSold,
    eventEndDate,
  )

  useEffect(() => {
    stripePromiseRef.current = loadStripe(spk)
  }, [spk])

  const queryClient = useQueryClient()
  const createMutation = useCreateTicketPurchase()
  const updateMutation = useUpdateTicketPurchase()

  // Reset isNewCard when userDefaultPaymentMethodExpired changes (e.g., after adding new card)
  useEffect(() => {
    if (!userDefaultPaymentMethodExpired) {
      // User now has a valid card, reset isNewCard to false
      setIsNewCard(false)
    }
  }, [userDefaultPaymentMethodExpired])
  const [hasAutoInitialized, setHasAutoInitialized] = useState(false)
  const [hasAutoOpened, setHasAutoOpened] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [cancellingTicketIds, setCancellingTicketIds] = useState<number[]>([])
  const [showManageModal, setShowManageModal] = useState(false)

  const initializeTicketPurchase = useCallback(
    async (e?: React.MouseEvent, isManualClick: boolean = false) => {
      // Sliding scale opens the dialog for price selection; payment is
      // triggered from inside the dialog
      if (isSlidingScale) {
        if (isManualClick) setHasAutoInitialized(false)
        setTicketPurchase(null)
        setShowDialog(true)
        return
      }

      // Use current prop values, not stale state, when initializing
      // If user has a valid saved card (not expired), use it; otherwise use new card
      // Only use new card if: user explicitly wants new card OR card is expired
      const shouldUseNewCard =
        userDefaultPaymentMethodExpired === true || isNewCard === true

      // If this is a manual click (not auto-initialized), ensure dialog opens after creation
      if (isManualClick) {
        setHasAutoInitialized(false)
      }

      // If user has already purchased tickets, just show the modal
      // The ticket purchase will be created when the modal opens
      if (ticketsPurchasedByUserCount > 0) {
        setShowDialog(true)
        // Create ticket purchase in the background
        createMutation.mutate({
          bandId,
          params: {
            ticket_purchase: {
              post_id: postId,
              is_new_card: shouldUseNewCard,
              quantity: quantity,
              ...(isSlidingScale && chosenPrice != null ? { chosen_price: chosenPrice } : {}),
            },
          },
        })
        return
      }

      // For users without existing tickets, create the ticket purchase and wait for it
      await createMutation.mutateAsync({
        bandId,
        params: {
          ticket_purchase: {
            post_id: postId,
            is_new_card: shouldUseNewCard,
            quantity: quantity,
            ...(isSlidingScale && chosenPrice != null ? { chosen_price: chosenPrice } : {}),
          },
        },
      })
    },
    [
      bandId,
      postId,
      isNewCard,
      userDefaultPaymentMethodExpired,
      createMutation,
      isSlidingScale,
      chosenPrice,
      quantity,
      ticketsPurchasedByUserCount,
    ],
  )

  // Re-initialize ticket purchase with is_new_card: true when user clicks "Use a different card"
  const switchToNewCard = useCallback(() => {
    const shouldUseNewCard = true
    createMutation.mutate({
      bandId,
      params: {
        ticket_purchase: {
          post_id: postId,
          is_new_card: shouldUseNewCard,
          quantity: quantity,
        },
      },
    })
  }, [bandId, postId, quantity, createMutation])

  const claimFreeTicket = useCallback(
    async (e?: React.MouseEvent) => {
      try {
        await createMutation.mutateAsync({
          bandId,
          params: {
            ticket_purchase: {
              post_id: postId,
              is_new_card: false,
              quantity: quantity,
            },
          },
        })
      } catch {
        alert("There was an error claiming your free ticket. Please try again or contact support@bandnada.com")
      }
    },
    [bandId, postId, quantity, createMutation],
  )

  // Handle ticket purchase state based on hook data
  useEffect(() => {
    // Don't run effects while loading ticket data
    if (isLoadingTicket) {
      return
    }

    // Auto-open dialog if user has an incomplete ticket purchase and tickets are available AND autoOpenDialog is enabled
    // Only auto-open once to prevent it from reopening after user closes it
    if (
      autoOpenDialog &&
      !hasAutoOpened &&
      userHasAnyTicketPurchase &&
      ticketsPurchasedByUserCount === 0 &&
      ticketsAvailable
    ) {
      // Set the existing ticket purchase data from the hook
      if (userTicket) {
        setTicketPurchase({
          id: userTicket.id,
          status: userTicket.status,
          payment_method: userTicket.payment_method,
          client_secret: userTicket.client_secret,
        })
        setShowDialog(true)
        setHasAutoOpened(true)
        setHasAutoInitialized(false) // Reset when we have existing purchase
      }
    }
    // Auto-initialize ticket purchase for new users (no existing purchase) when autoOpenDialog is true
    // Only auto-initialize once to prevent it from reopening after user closes it
    else if (
      autoOpenDialog &&
      !hasAutoOpened &&
      !userHasAnyTicketPurchase &&
      ticketsPurchasedByUserCount === 0 &&
      ticketsAvailable &&
      !hasAutoInitialized &&
      createMutation.status !== "loading" &&
      createMutation.status !== "success"
    ) {
      // Initialize a new ticket purchase for newly registered users
      setHasAutoInitialized(true)
      setHasAutoOpened(true)
      initializeTicketPurchase()
    }
  }, [
    isLoadingTicket,
    ticketsPurchasedByUserCount,
    userHasAnyTicketPurchase,
    userTicketPurchaseStatus,
    ticketsAvailable,
    autoOpenDialog,
    userTicket,
    hasAutoInitialized,
    hasAutoOpened,
    createMutation.status,
    initializeTicketPurchase,
  ])

  const cancelAndRefundTicket = useCallback(async () => {
    // Get all completed ticket purchases to cancel
    const completedTicketPurchases = userTicketPurchases.filter((ticket) =>
      TicketPurchaseUtils.isCompleted(ticket),
    )

    if (
      window.confirm(
        `Are you sure you want to cancel and refund your ${completedTicketPurchases.length} ${completedTicketPurchases.length === 1 ? "ticket" : "tickets"}? This action cannot be undone.`,
      )
    ) {
      setIsCancelling(true)
      try {
        // Cancel all completed ticket purchases and wait for all to complete
        await Promise.all(
          completedTicketPurchases.map((ticket) =>
            updateMutation.mutateAsync({
              bandId,
              ticketPurchaseId: ticket.id,
              params: {
                ticket_purchase: {
                  status: "cancelled",
                },
              },
            }),
          ),
        )

        // Explicitly invalidate the myTickets cache to ensure UI updates
        await queryClient.invalidateQueries(
          ticketPurchaseQueryKeys.myTickets(postId),
        )

        // Show success toast notification
        toast.success("Your tickets have been refunded")
      } catch (error) {
        console.error("Error cancelling tickets:", error)
        alert(
          "There was an error cancelling your tickets. Please try again or contact support.",
        )
      } finally {
        setIsCancelling(false)
      }
    }
  }, [bandId, updateMutation, userTicketPurchases, queryClient, postId])

  const cancelSingleTicket = useCallback(async (ticket: TicketPurchase) => {
    const confirmMsg = isFreeTicket
      ? `Cancel ticket #${ticket.ticket_id}? This cannot be undone.`
      : `Cancel and refund ticket #${ticket.ticket_id}? This cannot be undone.`
    if (!window.confirm(confirmMsg)) return
    setCancellingTicketIds((prev) => [...prev, ticket.id])
    try {
      await updateMutation.mutateAsync({
        bandId,
        ticketPurchaseId: ticket.id,
        params: { ticket_purchase: { status: "cancelled" } },
      })
      await queryClient.invalidateQueries(ticketPurchaseQueryKeys.myTickets(postId))
      toast.success(isFreeTicket ? "Your ticket has been cancelled" : "Your ticket has been refunded")
    } catch (error) {
      console.error("Error cancelling ticket:", error)
      alert("There was an error cancelling your ticket. Please try again or contact support.")
    } finally {
      setCancellingTicketIds((prev) => prev.filter((id) => id !== ticket.id))
    }
  }, [bandId, updateMutation, queryClient, postId])

  useEffect(() => {
    if (createMutation.status === "success") {
      if (createMutation.data.success) {
        // Use the first ticket purchase from the array
        const firstTicketPurchase = createMutation.data.ticket_purchases?.[0]
        if (firstTicketPurchase) {
          // Only open dialog if this ticket purchase is for this component's postId
          // This prevents multiple dialogs from opening when there are multiple components on the page
          if (firstTicketPurchase.post_id === postId) {
            // Free tickets come back already charged — no payment dialog needed
            if (firstTicketPurchase.status === "charged" && isFreeTicket) {
              queryClient.invalidateQueries(ticketPurchaseQueryKeys.myTickets(postId))
              setShowDialog(false)
              toast.success("Your free ticket has been claimed!")
              setBandInterestTicketPurchaseId(firstTicketPurchase.id)
              setShowBandInterestPrompt(true)
              return
            }

            // Update ticket purchase state with the new data
            // When isNewCard is true, this should have payment_method: null and a new client_secret
            // When isNewCard is false, this should have payment_method set to the saved card
            const newTicketPurchaseState = {
              id: firstTicketPurchase.id,
              status: firstTicketPurchase.status,
              payment_method: firstTicketPurchase.payment_method, // Should be set when isNewCard is false
              client_secret: firstTicketPurchase.client_secret, // Should be a new secret when isNewCard is true
            }
            setTicketPurchase(newTicketPurchaseState)
            // If we have a payment_method from the backend, ensure isNewCard is false
            if (newTicketPurchaseState.payment_method && isNewCard) {
              setIsNewCard(false)
            }
            // Open dialog after ticket purchase is created (both for auto and manual)
            // Coordination is handled at the parent level via autoOpenDialog prop
            // Manual clicks are always allowed
            setShowDialog(true)
            // If this was auto-initialized, mark that we've auto-opened
            if (hasAutoInitialized) {
              setHasAutoOpened(true)
            }
          }
        }
      } else {
        // Reset isNewCard on error so user can try again
        setIsNewCard(false)
        alert(
          "We're sorry! There was an error with purchasing a ticket. Reload the page and try again or email support@bandnada.com if you've already done that.",
        )
        console.error("Failed to get client secret: ", createMutation.data)
      }
    } else if (createMutation.status === "error") {
      // Reset isNewCard on error so user can try again
      setIsNewCard(false)
      console.error(
        "Ticket purchase create/update failed:",
        createMutation.error,
      )
      alert(
        "We're sorry! There was an error updating your payment method. Please try again or reload the page.",
      )
    }
  }, [
    createMutation.status,
    createMutation.data,
    createMutation.error,
    hasAutoInitialized,
    setIsNewCard,
    postId,
  ])

  // Track previous tickets count to detect successful purchases
  const previousTicketsCountRef = useRef<number | null>(null)

  // Reset quantity to 1 after a successful purchase
  useEffect(() => {
    // Initialize ref on first render (don't reset quantity on mount)
    if (previousTicketsCountRef.current === null) {
      previousTicketsCountRef.current = ticketsPurchasedByUserCount
      return
    }

    // Only reset if the count increased (indicating a successful purchase)
    if (ticketsPurchasedByUserCount > previousTicketsCountRef.current) {
      setQuantity(1)
    }
    // Update the ref to track the current count for next comparison
    previousTicketsCountRef.current = ticketsPurchasedByUserCount
  }, [ticketsPurchasedByUserCount])

  // Reset checkoutCompleted when dialog opens for a new purchase
  useEffect(() => {
    if (showDialog) {
      setCheckoutCompleted(false)
    }
  }, [showDialog])

  // Prompt for who they're here to see right after a paid purchase completes
  useEffect(() => {
    if (checkoutCompleted) {
      setBandInterestTicketPurchaseId(ticketPurchase?.id ?? null)
      setShowBandInterestPrompt(true)
    }
  }, [checkoutCompleted])

  // Button should be disabled if tickets are sold out, user has reached the max purchase limit, event is over, or still loading ticket data
  const isDisabled =
    !ticketsAvailable ||
    ticketsPurchasedByUserCount >= effectiveMaxTicketsPerUser ||
    eventOver ||
    isLoadingTicket

  // Calculate how many more tickets the user can buy
  // Limited by both tickets available for the show and the max purchase limit per user
  const maxTicketsAllowedToBuy =
    Math.min(ticketsAvailableForShow, effectiveMaxTicketsPerUser) -
    ticketsPurchasedByUserCount

  return (
    <div>
      <div className="grid grid-cols-12 mt-3 mb-2 relative">
        <div className="lg:col-span-9 col-span-12 text-base">
          <div className="flex flex-col items-start space-y-2">
            {availabilityMessage && !(eventOver && ticketsPurchasedByUserCount > 0) && (
              <div
                className={`text-sm ${availabilityMessage === "Low ticket warning!" ? "text-red-600 font-semibold" : "text-gray-600"}`}
              >
                {availabilityMessage}
              </div>
            )}

            {ticketsPurchasedByUserCount > 0 ? (
              <Button
                className="w-auto px-4 mt-2"
                onClick={() => setShowManageModal(true)}
                bg="bg-gray-900"
              >
                Manage my {ticketsPurchasedByUserCount} {ticketsPurchasedByUserCount === 1 ? "ticket" : "tickets"}
              </Button>
            ) : isFreeTicket ? (
              <Button
                className="w-auto px-4 mt-2"
                onClick={(e) => claimFreeTicket(e)}
                disabled={isDisabled || createMutation.isLoading}
                bg="bg-green-700"
              >
                {isLoadingTicket || createMutation.isLoading
                  ? "Loading..."
                  : eventOver
                    ? "Event Ended"
                    : !ticketsAvailable
                      ? "Sold Out"
                      : "Get Free Ticket / RSVP"}
              </Button>
            ) : (
              <Button
                className="w-auto px-4 mt-2"
                onClick={(e) => initializeTicketPurchase(e, true)}
                disabled={isDisabled}
                bg="bg-green-700"
              >
                {isLoadingTicket
                  ? "Loading..."
                  : eventOver
                    ? "Event Ended"
                    : !ticketsAvailable
                      ? "Sold Out"
                      : isSlidingScale
                        ? `RSVP / Buy Ticket ${formatPrice(ticketPrice)}–${formatPrice(priceMax ?? 0)}`
                        : `Buy ${formatPrice(ticketPrice)} Ticket / RSVP`}
              </Button>
            )}
            <AlternatePricingDisplay concert={concert} />
          </div>
        </div>
      </div>
      {showManageModal && (
        <Dialog
          isOpen={showManageModal}
          onDismiss={() => setShowManageModal(false)}
          aria-label="Manage your tickets"
        >
          <div className="flex justify-end">
            <button
              className="close-button focus:outline-none text-lg"
              onClick={() => setShowManageModal(false)}
            >
              <span aria-hidden>×</span>
            </button>
          </div>

          <h1 className="border-b-4 border-black leading-tight mb-4 pb-1">
            My Tickets for {concert.title}
          </h1>

          <div className="mb-4">
            <div
              className="text-green-700 text-lg font-extrabold"
              style={{ textShadow: "0 1px 2px rgba(0, 0, 0, 0.15)" }}
            >
              {userName}
            </div>
            <div
              className="text-green-700 text-lg font-bold"
              style={{ textShadow: "0 1px 2px rgba(0, 0, 0, 0.15)" }}
            >
              {user?.email}
            </div>
          </div>

          <div className="space-y-4 mb-6">
            {userTicketPurchases
              .filter((t) => TicketPurchaseUtils.isCompleted(t))
              .map((t) => (
                <div key={t.id} className="flex items-center justify-between border border-gray-200 rounded px-3 py-2 text-sm mb-4">
                  <span className="font-mono text-gray-600">#{t.ticket_id}</span>
                  <div className="flex items-center gap-3">
                    {t.amount > 0 ? (
                      <span className="text-gray-500 text-xs">{formatPrice(t.amount)}</span>
                    ) : (
                      <span className="text-gray-500 text-xs">Free</span>
                    )}
                    {t.redeemed ? (
                      <span className="text-gray-400 text-xs">Redeemed</span>
                    ) : (
                      <span className="text-gray-400 text-xs">Unredeemed</span>
                    )}
                    {!eventStarted && !t.redeemed && (
                      <button
                        onClick={() => cancelSingleTicket(t)}
                        disabled={cancellingTicketIds.includes(t.id)}
                        className="text-xs text-red-600 italic focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {cancellingTicketIds.includes(t.id) ? "Cancelling..." : isFreeTicket ? "Cancel" : "Cancel & Refund"}
                      </button>
                    )}
                  </div>
                </div>
              ))}
          </div>

          <BandInterestPrompt
            isOpen={true}
            postId={postId}
            ticketPurchaseId={userTicket?.id}
            eventPerformances={(concert as any)?.event_performances}
            initialSelectedIds={userTicket?.event_performance_interest_ids}
          />

          {!eventOver && maxTicketsAllowedToBuy > 0 && (
            <button
              className="text-blue-600 text-sm underline"
              onClick={(e) => {
                setShowManageModal(false)
                if (isFreeTicket) {
                  claimFreeTicket(e)
                } else {
                  initializeTicketPurchase(e, true)
                }
              }}
            >
              {isFreeTicket
                ? "Get another free ticket"
                : `Buy more tickets — ${formatPrice(ticketPrice)}`}
            </button>
          )}
        </Dialog>
      )}

      {showDialog && (
        <Dialog
          isOpen={showDialog}
          onDismiss={closeDialog}
          aria-label="Purchase ticket for the event"
        >
          <div className="flex justify-end">
            <button
              className="close-button focus:outline-none text-lg"
              onClick={closeDialog}
            >
              <span className="focus:outline-none" aria-hidden>
                ×
              </span>
            </button>
          </div>

          <div className="grid grid-cols-12">
            <div className="col-span-12">
              <h1 className="border-b-4 border-black mb-6 pb-1 text-xl">
                {ticketPurchase?.status === "initialized" ||
                ticketPurchase?.status === "confirmed_by_user"
                  ? "Complete Purchase of Your"
                  : "Purchase"}{" "}
                Ticket for <span className="font-bold">{bandName}</span>
              </h1>
            </div>
          </div>
          <div className="grid grid-cols-12">
            <div className="col-span-12">
              <TicketEventInfo
                concert={concert}
                ticketPrice={ticketPrice}
                ticketPriceMax={isSlidingScale ? priceMax : null}
                isSlidingScale={isSlidingScale}
                chosenPrice={chosenPrice}
                onChosenPriceChange={!checkoutCompleted ? setChosenPrice : undefined}
                quantity={quantity}
                onQuantityChange={setQuantity}
                maxTicketsAllowedToBuy={maxTicketsAllowedToBuy}
                ticketsPurchasedByUserCount={ticketsPurchasedByUserCount}
                showQuantitySelector={!checkoutCompleted}
                checkoutCompleted={checkoutCompleted}
              />

              <Elements stripe={stripePromiseRef.current}>
                <CheckoutForm
                  userName={userName}
                  userEmail={user?.email}
                  lastFour={lastFour}
                  setLastFour={setLastFour}
                  bandId={bandId}
                  ticketPurchaseId={ticketPurchase?.id}
                  paymentMethod={ticketPurchase?.payment_method}
                  clientSecret={ticketPurchase?.client_secret}
                  ticketPrice={
                    isSlidingScale ? (chosenPrice ?? ticketPrice) : ticketPrice
                  }
                  quantity={quantity}
                  postId={postId}
                  isNewCard={isNewCard}
                  setIsNewCard={setIsNewCard}
                  onSwitchToNewCard={switchToNewCard}
                  onCheckoutComplete={() => setCheckoutCompleted(true)}
                  isCreatingNewCard={isNewCard && createMutation.isLoading}
                  onBeforeCheckout={isSlidingScale && !ticketPurchase ? async () => {
                    const shouldUseNewCard = userDefaultPaymentMethodExpired === true || isNewCard === true
                    const result = await createMutation.mutateAsync({
                      bandId,
                      params: {
                        ticket_purchase: {
                          post_id: postId,
                          is_new_card: shouldUseNewCard,
                          quantity,
                          chosen_price: chosenPrice ?? ticketPrice,
                        },
                      },
                    })
                    const tp = result?.ticket_purchases?.[0]
                    return { id: tp.id, payment_method: tp.payment_method, client_secret: tp.client_secret }
                  } : undefined}
                />
              </Elements>

              {checkoutCompleted && (
                <BandInterestPrompt
                  isOpen={showBandInterestPrompt}
                  postId={postId}
                  ticketPurchaseId={bandInterestTicketPurchaseId}
                  eventPerformances={(concert as any)?.event_performances}
                />
              )}
            </div>
          </div>
          {!eventOver && !checkoutCompleted && (
            <div className="mt-6">
              <div className="flex text-gray-700 text-sm">
                <div>
                  On the list/playing/or just want to pay at the door? RSVP to
                  stay in the know.
                </div>
                <InterestedBlock
                  renderMode="text"
                  postId={concert.id}
                  maxRsvps={concert.max_rsvps}
                />
              </div>
            </div>
          )}
        </Dialog>
      )}

      {/* Free tickets are claimed without opening the purchase dialog above,
          so the band-interest prompt needs its own small dialog here - but
          only when there's an actual >=2 picker to show. With 0 or 1
          qualifying performers, BandInterestPrompt renders nothing (it
          auto-saves the sole performer itself), so wrapping it in a Dialog
          would just pop up an empty box. Mount it without the dialog chrome
          in that case so the auto-save effect still runs. */}
      {!showDialog && showBandInterestPrompt && (
        qualifyingBandInterestLineup((concert as any)?.event_performances).length >= 2 ? (
          <Dialog
            isOpen={showBandInterestPrompt}
            onDismiss={() => setShowBandInterestPrompt(false)}
            aria-label="Which bands are you here to see?"
            className="rounded-md max-w-lg w-full"
          >
            <BandInterestPrompt
              isOpen={showBandInterestPrompt}
              postId={postId}
              ticketPurchaseId={bandInterestTicketPurchaseId}
              eventPerformances={(concert as any)?.event_performances}
            />
          </Dialog>
        ) : (
          <BandInterestPrompt
            isOpen={showBandInterestPrompt}
            postId={postId}
            ticketPurchaseId={bandInterestTicketPurchaseId}
            eventPerformances={(concert as any)?.event_performances}
          />
        )
      )}
    </div>
  )
}

export default TicketPurchaseComponent
